import get from 'lodash.get';
import keyBy from 'lodash.keyby';
import mapValues from 'lodash.mapvalues';

// Global Types
import { AppState } from 'services/types';

// Global Selectors
// @ts-ignore
import {
    makeSelectEntityTypeValue,
    selectEntityTypeFirstDepth
} from 'services/entityTypes/selectors';
import { makeSelectError } from 'services/error/selectors';
// @ts-ignore
import { selectUsers } from 'services/people/selectors';

// Global Utils
import formatDropdownOptions from 'utils/format-dropdown-options';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';

// Local Types
import {
    PersonEmail,
    AssignedUser,
    Entity,
    EntitiesState,
    EntitiesProps,
    EntityUuid,
    EntityWithComputedFields
} from './types';

// Local Utils
import {
    getUsersEmails,
    getEntityHierarchy,
    getEntitySummedRemovals
} from './utils';

const selectEntityState = (state: AppState): EntitiesState => state.entities;

const selectEntityUuid = (
    state: AppState,
    props: EntitiesProps
): string | undefined => props.entityUuid;

const selectEntityIdProp = (
    state: AppState,
    props: EntitiesProps
): number | undefined => props.entityId;

const selectEntityUpdates = createDeepEqualSelector(
    [selectEntityState],
    (entitiesStateState) => entitiesStateState.updates
);

const selectEntityErrors = createDeepEqualSelector(
    [selectEntityState],
    (entitiesStateState) => entitiesStateState.errors
);

const selectEntityErrorsKeyedByUuid = createDeepEqualSelector(
    [selectEntityErrors, selectEntityUpdates, makeSelectError('ENTITIES_SAVE')],
    (entityErrorsState, entityUpdatesState, errorState) => {
        const entityUpdatesUuids = Object.keys(entityUpdatesState);

        if (entityErrorsState.length) {
            return entityErrorsState.reduce<Record<string, string>>(
                (accumulator, currentValue) => {
                    const { message, uuids } = currentValue;
                    uuids.forEach((uuid) => {
                        accumulator[uuid] = message;
                    });
                    return accumulator;
                },
                {}
            );
        }

        if (errorState) {
            const errorsKeyedByUuid: Record<string, string> = {};
            entityUpdatesUuids.forEach((uuid: string) => {
                errorsKeyedByUuid[uuid] = errorState;
            });
            return errorsKeyedByUuid;
        }

        return {};
    }
);

const selectEntityErrorsExist = createDeepEqualSelector(
    [selectEntityErrorsKeyedByUuid],
    (entityErrorsKeyedByUuidState) =>
        !!Object.keys(entityErrorsKeyedByUuidState).length
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectEntityErrorMessage = () =>
    createDeepEqualSelector(
        [selectEntityErrorsKeyedByUuid, selectEntityUuid],
        (entityErrorsKeyedByUuidState, entityUuidProp) => {
            if (!entityUuidProp) {
                return undefined;
            }
            return get(entityErrorsKeyedByUuidState, entityUuidProp);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectEntityErrorMessageExists = () =>
    createDeepEqualSelector(
        [makeSelectEntityErrorMessage()],
        (entityErrorMessage) => !!entityErrorMessage
    );

const selectEntitiesKeyedByUuid = createDeepEqualSelector(
    [selectEntityState, selectUsers],
    (
        entitiesStateState,
        users: Record<PersonEmail, any>
    ): Record<EntityUuid, EntityWithComputedFields> => {
        const { data, updates, deletes } = entitiesStateState;

        const entities = {
            ...data,
            ...updates
        };

        deletes.forEach((uuid) => {
            const entity = entities[uuid];
            if (entity) {
                entities[uuid] = {
                    ...entities[uuid],
                    archivedAt: 'foobar'
                };
            }
        });

        const entitiesKeyById = keyBy(entities, 'id');

        return mapValues(entities, (entity) => {
            const { inspectionUserEmails, timesheetUserEmails } = entity;

            const entityHierarchy = getEntityHierarchy(entity, entitiesKeyById);

            const summedRemovals = getEntitySummedRemovals(entity.removals);

            return {
                ...entity,
                path: entityHierarchy.path,
                pathNames: entityHierarchy.pathNames,
                archivedAt: entityHierarchy.archivedAt,
                removedTotalArea: summedRemovals.sumTotalArea,
                removedNumberOfTrees: summedRemovals.sumNumberOfTrees,
                inspectionUserFullNames: getUsersEmails(
                    users,
                    inspectionUserEmails
                ),
                timesheetUserFullNames: getUsersEmails(
                    users,
                    timesheetUserEmails
                )
            };
        });
    }
);

const selectEntitiesArray = createDeepEqualSelector(
    selectEntitiesKeyedByUuid,
    (entitiesKeyedByUuid) => Object.values(entitiesKeyedByUuid)
);

const selectActiveEntitiesArray = createDeepEqualSelector(
    [selectEntitiesArray],
    (entitiesArray) =>
        entitiesArray.filter(
            (entity) =>
                !entity.archivedAt && !entity.deletedAt && entity.createdAt
        )
);

const selectEntities = createDeepEqualSelector(
    [selectEntitiesArray],
    (entitiesArray) => keyBy(entitiesArray, 'id')
);

const selectUnsavedEntities = createDeepEqualSelector(
    [selectEntityState],
    (entitiesStateState) => {
        const { updates, deletes } = entitiesStateState;

        const updatesArray = Object.values(updates);

        const create: Entity[] = [];

        updatesArray.forEach((value) => {
            const {
                inspectionUserEmails,
                timesheetUserEmails,
                numberOfTrees,
                rowSpacing,
                totalArea
            } = value;

            const assignedUsers: AssignedUser[] = [];

            if (inspectionUserEmails && inspectionUserEmails.length) {
                inspectionUserEmails.forEach((email) => {
                    assignedUsers.push({
                        email,
                        jobType: 'INSPECTION'
                    });
                });
            }

            if (timesheetUserEmails && timesheetUserEmails.length) {
                timesheetUserEmails.forEach((email) => {
                    assignedUsers.push({
                        email,
                        jobType: 'TIMESHEET'
                    });
                });
            }

            const hasTreeData = numberOfTrees || numberOfTrees === 0;
            const formattedEntity = {
                name: value.name,
                uuid: value.uuid,
                entityTypeId: value.entityTypeId,
                seasonId: value.seasonId,
                costCentre: value.costCentre,
                defaultVarietyId: value.defaultVarietyId,
                fieldValue0: value.fieldValue0,
                fieldValue1: value.fieldValue1,
                fieldValue2: value.fieldValue2,
                fieldValue3: value.fieldValue3,
                fieldValue4: value.fieldValue4,
                managementArea: value.managementArea,
                numberOfTrees: hasTreeData ? numberOfTrees : undefined,
                orchardRpin: value.orchardRpin,
                parentEntityId: value.parentEntityId,
                productionSite: value.productionSite,
                totalArea: hasTreeData ? totalArea : undefined,
                assignedUsers,
                plantingDate: value.plantingDate,
                removals: hasTreeData ? value.removals : undefined,
                isNonProductive:
                    typeof value.isNonProductive === 'boolean'
                        ? value.isNonProductive
                        : undefined,
                rowSpacing
            };

            create.push(formattedEntity);
        });

        const hasCreates = !!create.length;
        const hasDeletes = !!deletes.length;

        if (!hasCreates && !hasDeletes) {
            return undefined;
        }

        return {
            ...(hasCreates
                ? {
                      create
                  }
                : {}),
            ...(hasDeletes
                ? {
                      delete: deletes
                  }
                : {})
        };
    }
);

const selectUnsavedEntitiesCount = createDeepEqualSelector(
    selectUnsavedEntities,
    (unsavedEntities) => {
        const createsCount = get(unsavedEntities, ['create', 'length'], 0);
        const deletesCount = get(unsavedEntities, ['delete', 'length'], 0);

        return createsCount + deletesCount;
    }
);

const selectUnsavedEntitiesExist = createDeepEqualSelector(
    selectUnsavedEntitiesCount,
    (unsavedEntitiesCount) => !!unsavedEntitiesCount
);

const selectEntity = createDeepEqualSelector(
    [
        selectEntitiesKeyedByUuid,
        selectEntityUuid,
        selectEntities,
        selectEntityIdProp
    ],
    (entities, entityUuid, entitiesKeyedById, entityIdProp) => {
        if (entityUuid) {
            return get(entities, entityUuid);
        }
        // Support legacy implimentation of entities
        if (entityIdProp) {
            return get(entitiesKeyedById, entityIdProp);
        }
        return undefined;
    }
);

const selectEntityFirstDepthOptions = createDeepEqualSelector(
    [selectActiveEntitiesArray, selectEntityTypeFirstDepth],
    (entitiesArray, entityType) => {
        if (!entityType) return [];

        return formatDropdownOptions(entitiesArray, {
            keyField: 'id',
            textField: 'name',
            filterPredicate: { entityTypeId: entityType.id }
        });
    }
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectEntityHasUnsavedChanges = () =>
    createDeepEqualSelector(
        [selectEntityUpdates, selectEntityUuid],
        (entitiesUpdatesState, entityUuidProp) => {
            if (!entityUuidProp) {
                return false;
            }
            return !!get(entitiesUpdatesState, entityUuidProp);
        }
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const makeSelectEntity = () => selectEntity;

const makeSelectEntityValue = (field: any): any =>
    createDeepEqualSelector(selectEntity, (entityState) =>
        get(entityState, field)
    );

const selectEntityId = createDeepEqualSelector(selectEntity, (entityState) =>
    get(entityState, 'id')
);

const makeSelectEntityId = (): typeof selectEntityId => selectEntityId;

const selectEntityParentEntityId = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'parentEntityId')
);

const makeSelectEntityParentEntityId = (): typeof selectEntityParentEntityId =>
    selectEntityParentEntityId;

const selectEntityParentEntityEntityTypeId = createDeepEqualSelector(
    [selectEntityParentEntityId, selectEntities],
    (entityParentEntityId, entitiesKeyedById) => {
        if (entityParentEntityId) {
            return get(entitiesKeyedById, [
                entityParentEntityId,
                'entityTypeId'
            ]);
        }
        return undefined;
    }
);

const makeSelectEntityParentEntityEntityTypeId =
    (): typeof selectEntityParentEntityEntityTypeId =>
        selectEntityParentEntityEntityTypeId;

const makeSelectEntityDefaultVarietyId = (): any =>
    createDeepEqualSelector(selectEntity, (entityState) =>
        get(entityState, 'defaultVarietyId')
    );

const selectEntityName = createDeepEqualSelector(selectEntity, (entityState) =>
    get(entityState, 'name')
);

const makeSelectEntityName = (): typeof selectEntityName => selectEntityName;

const selectEntityOrchardRpin = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'orchardRpin')
);

const makeSelectEntityOrchardRpin = (): typeof selectEntityOrchardRpin =>
    selectEntityOrchardRpin;

const selectParentEntityOrchardRpin = createDeepEqualSelector(
    [selectEntityParentEntityId, selectEntities],
    (entityParentEntityId, entitiesKeyedById) => {
        if (entityParentEntityId) {
            return get(entitiesKeyedById, [
                entityParentEntityId,
                'orchardRpin'
            ]);
        }
        return undefined;
    }
);

const makeSelectParentEntityOrchardRpin = (): typeof selectParentEntityOrchardRpin =>
    selectParentEntityOrchardRpin;

const selectEntityCostCentre = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'costCentre')
);

const makeSelectEntityCostCentre = (): typeof selectEntityCostCentre =>
    selectEntityCostCentre;

const selectEntityManagementArea = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'managementArea')
);

const makeSelectEntityManagementArea = (): typeof selectEntityManagementArea =>
    selectEntityManagementArea;

const selectEntityProductionSite = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'productionSite')
);

const makeSelectEntityProductionSite = (): typeof selectEntityProductionSite =>
    selectEntityProductionSite;

const selectEntityRowSpacing = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'rowSpacing')
);

const makeSelectEntityRowSpacing = (): typeof selectEntityRowSpacing =>
    selectEntityRowSpacing;

const selectEntityTotalArea = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'totalArea')
);

const makeSelectEntityTotalArea = (): typeof selectEntityTotalArea =>
    selectEntityTotalArea;

const selectEntityNumberOfTrees = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'numberOfTrees')
);

const makeSelectEntityNumberOfTrees = (): typeof selectEntityNumberOfTrees =>
    selectEntityNumberOfTrees;

const selectEntityFieldValue0 = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'fieldValue0')
);

const makeSelectEntityFieldValue0 = (): typeof selectEntityFieldValue0 =>
    selectEntityFieldValue0;

const selectEntityFieldValue1 = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'fieldValue1')
);

const makeSelectEntityFieldValue1 = (): typeof selectEntityFieldValue1 =>
    selectEntityFieldValue1;

const selectEntityFieldValue2 = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'fieldValue2')
);

const makeSelectEntityFieldValue2 = (): typeof selectEntityFieldValue2 =>
    selectEntityFieldValue2;

const selectEntityFieldValue3 = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'fieldValue3')
);

const makeSelectEntityFieldValue3 = (): typeof selectEntityFieldValue3 =>
    selectEntityFieldValue3;

const selectEntityFieldValue4 = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'fieldValue4')
);

const selectEntityPathNames = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'pathNames')
);

const selectEntityPathNamesKeyed = createDeepEqualSelector(
    [selectActiveEntitiesArray],
    (entitiesArrayState) =>
        keyBy(
            entitiesArrayState.filter((entity) => entity.pathNames),
            'pathNames'
        )
);

const makeSelectEntityPathNames = (): typeof selectEntityPathNames =>
    selectEntityPathNames;

const makeSelectEntityFieldValue4 = (): typeof selectEntityFieldValue4 =>
    selectEntityFieldValue4;

const selectEntityInspectionUserFullNames = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'inspectionUserFullNames')
);

const makeSelectEntityInspectionUserFullNames =
    (): typeof selectEntityInspectionUserFullNames =>
        selectEntityInspectionUserFullNames;

const selectEntityTimesheetUserFullNames = createDeepEqualSelector(
    selectEntity,
    (entityState) => get(entityState, 'timesheetUserFullNames')
);

const makeSelectEntityTimesheetUserFullNames =
    (): typeof selectEntityTimesheetUserFullNames =>
        selectEntityTimesheetUserFullNames;

const selectEntityPathEntity = createDeepEqualSelector(
    [
        makeSelectEntityValue('path'),
        selectEntities,
        makeSelectEntityTypeValue('depth')
    ],
    (entityPathState: any, entitiesState: any, entityTypeDepthState: any) => {
        if (!entityPathState || !entityTypeDepthState) return undefined;
        const entityIds = entityPathState.split('/');
        entityIds.shift();
        const entityId = entityIds[entityTypeDepthState - 1];
        return entitiesState[`${entityId}`];
    }
);

const makeSelectEntityPathEntityValue = (field: any): any =>
    createDeepEqualSelector(selectEntityPathEntity, (entityPathEntityState) =>
        get(entityPathEntityState, field)
    );

const selectEntityErrorMessages = createDeepEqualSelector(
    [selectEntityErrors, selectEntitiesKeyedByUuid],
    (entityErrorsState, entitiesKeyedByUuid) =>
        entityErrorsState.map((error) => {
            const entityNames = error.uuids.map(
                (uuid) => entitiesKeyedByUuid[uuid].name
            );

            return `${error.message} [${entityNames.join(', ')}]`;
        })
);

export {
    selectEntityUuid,
    selectEntityIdProp,
    selectEntityErrors,
    selectEntityErrorsExist,
    makeSelectEntityErrorMessage,
    makeSelectEntityErrorMessageExists,
    selectEntities,
    selectEntitiesArray,
    selectActiveEntitiesArray,
    selectEntityPathNamesKeyed,
    selectUnsavedEntities,
    selectUnsavedEntitiesCount,
    selectUnsavedEntitiesExist,
    makeSelectEntityHasUnsavedChanges,
    makeSelectEntity,
    makeSelectEntityId,
    selectEntityId,
    selectEntity,
    selectEntityFirstDepthOptions,
    makeSelectEntityParentEntityId,
    makeSelectEntityParentEntityEntityTypeId,
    makeSelectEntityValue,
    makeSelectEntityDefaultVarietyId,
    makeSelectEntityName,
    makeSelectEntityOrchardRpin,
    makeSelectParentEntityOrchardRpin,
    makeSelectEntityCostCentre,
    makeSelectEntityManagementArea,
    makeSelectEntityProductionSite,
    makeSelectEntityRowSpacing,
    makeSelectEntityTotalArea,
    makeSelectEntityNumberOfTrees,
    makeSelectEntityFieldValue0,
    makeSelectEntityFieldValue1,
    makeSelectEntityFieldValue2,
    makeSelectEntityFieldValue3,
    makeSelectEntityFieldValue4,
    makeSelectEntityPathNames,
    makeSelectEntityInspectionUserFullNames,
    makeSelectEntityTimesheetUserFullNames,
    selectEntityPathEntity,
    makeSelectEntityPathEntityValue,
    selectEntitiesKeyedByUuid,
    selectEntityErrorMessages
};
